import {
  createContext,
  useReducer,
  useContext,
  type ReactNode,
  type ChangeEvent,
} from 'react';

import { validateContent } from 'src/core/domain/errors';
import { getNewCreationDate } from 'src/core/utils/seekMaxUtil';
import type { Reply } from 'src/mapper/threadMapper';
import {
  createCommentReply,
  deleteReply,
  fetchReplies,
  type createCommentReplyParams,
  type fetchRepliesParams,
} from 'src/services/threadInteractionService';

import { useAnalytics } from './analytics';
import { useConfig } from './config';
import { useProfile } from './profile';

interface State {
  replies: Reply[];
  threadId: string;
  commentId: string;
  totalReplyCount: number;
  remainingReplyCount: number;
  hasNextPage: boolean;
  showReply: boolean;
  isLoading: boolean;
  showTextArea: boolean;
  textArea: string;
  discardState: boolean;
  error: string | null;
}

type Action =
  | {
      type: 'TOGGLE_SHOW_REPLIES';
      payload: { replies: Reply[]; showReply: boolean };
    }
  | {
      type: 'TOGGLE_TEXT_AREA';
      payload: { showTextArea: boolean };
    }
  | { type: 'UPDATE_TEXT_AREA'; payload: { textArea: string } }
  | { type: 'IS_LOADING' }
  | { type: 'COMPLETE_LOADING' }
  | {
      type: 'INIT';
      payload: { threadId: string; commentId: string; totalReplyCount: number };
    }
  | { type: 'SHOW_NEXT_PAGE'; payload: { replies: Reply[] } }
  | { type: 'ADD_REPLY'; payload: { reply: Reply } }
  | { type: 'CANCEL_REPLY' }
  | { type: 'DISCARD_REPLY' }
  | { type: 'DISMISS_ERROR' }
  | { type: 'SET_ERROR'; payload: { error: string } }
  | { type: 'DELETE_REPLY'; payload: { replyId: string } };

interface ReplyContextType {
  replyState: State;
  replyDispatch: React.Dispatch<Action>;
  toggleShowReplies: () => Promise<void>;
  showMore: () => Promise<void>;
  toggleReply: () => void;
  handleTextChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  handleCreateReply: () => Promise<string>;
  handleDeleteReply: (replyId: string) => void;
  handleCancel: () => void;
  confirmDiscard: () => void;
  cancelDiscard: () => void;
  dismissError: () => void;
  setError: (error: string) => void;
}

const ReplyContext = createContext<ReplyContextType | undefined>(undefined);

const repliesReducer = (replyState: State, action: Action): State => {
  switch (action.type) {
    case 'IS_LOADING':
      return {
        ...replyState,
        isLoading: true,
      };
    case 'COMPLETE_LOADING':
      return {
        ...replyState,
        isLoading: false,
      };
    case 'TOGGLE_SHOW_REPLIES':
      const {
        payload: { replies, showReply },
      } = action;

      return {
        ...replyState,
        showReply,
        replies,
        hasNextPage: replies.length < replyState.totalReplyCount,
      };
    case 'TOGGLE_TEXT_AREA':
      const {
        payload: { showTextArea },
      } = action;
      return {
        ...replyState,
        showTextArea,
      };
    case 'UPDATE_TEXT_AREA':
      const {
        payload: { textArea },
      } = action;
      return {
        ...replyState,
        textArea,
      };
    case 'INIT':
      const { threadId, commentId, totalReplyCount } = action.payload;
      return {
        threadId,
        commentId,
        totalReplyCount,
        showReply: false,
        hasNextPage: totalReplyCount > 0,
        replies: [],
        isLoading: false,
        remainingReplyCount: totalReplyCount,
        showTextArea: false,
        textArea: '',
        discardState: false,
        error: null,
      };
    case 'SHOW_NEXT_PAGE':
      const {
        payload: { replies: newReplies },
      } = action;
      return {
        ...replyState,
        replies: newReplies,
        hasNextPage: newReplies.length < replyState.totalReplyCount,
        remainingReplyCount: replyState.totalReplyCount - newReplies.length,
      };
    case 'ADD_REPLY':
      const {
        payload: { reply },
      } = action;
      return {
        ...replyState,
        replies: [reply, ...replyState.replies],
        totalReplyCount: replyState.totalReplyCount + 1,
        showReply: true,
      };
    case 'DELETE_REPLY':
      const {
        payload: { replyId },
      } = action;
      const filteredReplies = replyState.replies.filter(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (reply) => reply.id !== replyId,
      );
      return {
        ...replyState,
        replies: filteredReplies,
        totalReplyCount: filteredReplies.length,
        showReply: filteredReplies.length > 0,
      };
    case 'CANCEL_REPLY':
      return {
        ...replyState,
        discardState: true,
      };
    case 'DISCARD_REPLY':
      return {
        ...replyState,
        showTextArea: false,
        textArea: '',
        discardState: false,
      };
    case 'DISMISS_ERROR':
      return {
        ...replyState,
        error: null,
      };
    case 'SET_ERROR':
      const {
        payload: { error },
      } = action;
      return {
        ...replyState,
        error,
      };
    default:
      throw new Error('Unknown action type');
  }
};

interface RepliesProviderProps {
  children: ReactNode;
}

export const RepliesProvider: React.FC<RepliesProviderProps> = ({
  children,
}) => {
  const config = useConfig();
  const { trackAnalyticsInteraction } = useAnalytics();
  const {
    state: { author },
  } = useProfile();
  const [replyState, replyDispatch] = useReducer(repliesReducer, {
    replies: [],
    threadId: '',
    commentId: '',
    totalReplyCount: 0,
    hasNextPage: false,
    showReply: false,
    isLoading: false,
    remainingReplyCount: 0,
    showTextArea: false,
    textArea: '',
    discardState: false,
    error: null,
  });

  const toggleReply = () => {
    replyDispatch({
      type: 'TOGGLE_TEXT_AREA',
      payload: { showTextArea: !replyState.showTextArea },
    });
  };

  const toggleShowReplies = async () => {
    const interactionType = replyState.showReply
      ? 'HideReplies'
      : 'ShowReplies';

    if (replyState.replies.length > 0) {
      replyDispatch({
        type: 'TOGGLE_SHOW_REPLIES',
        payload: {
          replies: replyState.replies,
          showReply: !replyState.showReply,
        },
      });
      trackAnalyticsInteraction(interactionType, {
        threadId: replyState.threadId,
        commentId: replyState.commentId,
        linkPosition: 'thread comment',
        linkAction: replyState.showReply ? 'hide replies' : 'show replies',
      });
    } else {
      replyDispatch({ type: 'IS_LOADING' });
      const params: fetchRepliesParams = {
        commentId: replyState.commentId,
        config,
      };
      const newReplies = await fetchReplies(params);
      replyDispatch({
        type: 'TOGGLE_SHOW_REPLIES',
        payload: { replies: newReplies, showReply: !replyState.showReply },
      });

      trackAnalyticsInteraction(interactionType, {
        threadId: replyState.threadId,
        commentId: replyState.commentId,
        linkPosition: 'thread comment',
        linkAction: replyState.showReply ? 'hide replies' : 'show replies',
      });
      replyDispatch({ type: 'COMPLETE_LOADING' });
    }
  };

  const showMore = async () => {
    replyDispatch({ type: 'IS_LOADING' });
    const params: fetchRepliesParams = {
      commentId: replyState.commentId,
      cursor: replyState.replies.length,
      config,
    };
    const newReplies = await fetchReplies(params);
    replyDispatch({
      type: 'SHOW_NEXT_PAGE',
      payload: { replies: [...replyState.replies, ...newReplies] },
    });
    trackAnalyticsInteraction('MoreThreadComment', {
      threadId: replyState.threadId,
      commentId: replyState.commentId,
      totalCommentsLoaded: replyState.replies.length + newReplies.length,
    });
    replyDispatch({ type: 'COMPLETE_LOADING' });
  };

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    replyDispatch({
      type: 'UPDATE_TEXT_AREA',
      payload: { textArea: event.target.value },
    });
  };

  const handleCreateReply = async () => {
    validateContent(replyState.textArea);
    const { threadId, commentId, textArea, replies } = replyState;

    const replyParams: createCommentReplyParams = {
      threadId,
      commentId,
      content: textArea,
    };
    const result = await createCommentReply(replyParams);
    if (result.__typename === 'CreateSeekMaxReplySuccess') {
      const reply: Reply = {
        id: result.id,
        content: textArea,
        author,
        creationDate: getNewCreationDate().shortLabel,
      };
      replyDispatch({
        type: 'ADD_REPLY',
        payload: { reply },
      });
      showMore();
      replyDispatch({
        type: 'TOGGLE_TEXT_AREA',
        payload: { showTextArea: false },
      });
      handleTextChange({
        target: { value: '' },
      } as ChangeEvent<HTMLTextAreaElement>);
      return result.id;
    }
    replyDispatch({
      type: 'TOGGLE_TEXT_AREA',
      payload: { showTextArea: false },
    });
    replyDispatch({
      type: 'TOGGLE_SHOW_REPLIES',
      payload: { replies, showReply: true },
    });
    return '';
  };

  const handleDeleteReply = async (replyId: string) => {
    replyDispatch({ type: 'IS_LOADING' });
    const result = await deleteReply({ replyId });
    if (result.__typename === 'DeleteSeekMaxReplySuccess') {
      replyDispatch({
        type: 'DELETE_REPLY',
        payload: { replyId },
      });
    }
    replyDispatch({ type: 'COMPLETE_LOADING' });
  };

  const handleCancel = () => {
    if (replyState.textArea.length > 0) {
      replyDispatch({ type: 'CANCEL_REPLY' });
    } else {
      replyDispatch({ type: 'DISCARD_REPLY' });
    }
  };

  const confirmDiscard = () => {
    replyDispatch({ type: 'DISCARD_REPLY' });
  };

  const cancelDiscard = () => {
    replyDispatch({ type: 'DISCARD_REPLY' });
  };

  const dismissError = () => {
    replyDispatch({ type: 'DISMISS_ERROR' });
  };

  const setError = (error: string) => {
    replyDispatch({ type: 'SET_ERROR', payload: { error } });
  };

  return (
    <ReplyContext.Provider
      value={{
        replyState,
        replyDispatch,
        toggleShowReplies,
        showMore,
        toggleReply,
        handleTextChange,
        handleCreateReply,
        handleDeleteReply,
        handleCancel,
        confirmDiscard,
        cancelDiscard,
        dismissError,
        setError,
      }}
    >
      {children}
    </ReplyContext.Provider>
  );
};

export const useReplies = (): ReplyContextType => {
  const context = useContext(ReplyContext);
  if (!context) {
    throw new Error('useReplies must be used within a RepliesProvider');
  }
  return context;
};
